import React, { createContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiService, isMsConnectionRequired } from "../api/apiService";
import { useAuth } from "../hooks/useAuth";
import { tozsamoscKonta } from "../hooks/usePamiecPowloki";
import { stripHtml } from "../utils/htmlText";
import {
    KOD_TEAMS,
    kodBleduTeams,
    opisBleduTeams,
    czatOznaczonyNiedostepnym,
    powodNiedostepnosciCzatu,
} from "../utils/teamsBlad";
import { przelaczReakcjeZSerwerem } from "../utils/teamsReakcje";

export const TeamsChatContext = createContext(null);

/* Odstępy odpytywania. Microsoft Graph nie przysyła nam niczego sam — subskrypcja
   powiadomień wymaga adresu HTTPS osiągalnego z internetu, pod który Microsoft zapuka,
   a to decyzja o wystawieniu aplikacji na zewnątrz, nie zmiana w kodzie. Dopóki jej nie ma,
   „natychmiast" osiągamy inaczej: odświeżeniem w chwili, gdy człowiek wraca do karty. */
const POLL_INTERVAL_CHAT_LIST = 8000;  // 8s — panel otwarty
const POLL_INTERVAL_MESSAGES = 2500;   // 2.5s — otwarta rozmowa (to tu widać opóźnienie)
const POLL_INTERVAL_BACKGROUND = 12000; // 12s — bąbelek zwinięty, ale karta widoczna

/* Odpytywanie ukrytej karty to czyste marnowanie limitu żądań Graph — a właśnie limit był
   powodem ostrożnych odstępów. Zamiast tego: cisza, gdy nikt nie patrzy, i natychmiastowe
   odświeżenie w chwili powrotu. */
const czyKartaWidoczna = () => typeof document === "undefined" || !document.hidden;

/* Wycofanie przy odmowie. Skrócenie odstępów bez tego byłoby lekkomyślne: Graph odpowiada
   wtedy 429, a widget zamieniłby się w generator błędów. */
const MNOZNIK_WYCOFANIA = 2;
const MAKS_WYCOFANIE_MS = 60000;
const POLL_INTERVAL_PRESENCE = 60000;  // 60s — własny status obecności

// Podgląd wiadomości z Graph bywa HTML (np. "<p>najs</p>") — czyścimy do czystego tekstu na powiadomienia.
// Parsowanie idzie przez oddzielony dokument (utils/htmlText): `innerHTML` wykonywał treść wiadomości
// od nadawcy zewnętrznego (audyt 2026-08-23, A16-02).
const stripHtmlPreview = (html) => stripHtml(html).trim();

export const TeamsChatProvider = ({ children }) => {
    const { user } = useAuth();

    // Connection state
    const [isConnected, setIsConnected] = useState(false);
    const [connectionChecked, setConnectionChecked] = useState(false);
    const [needsReconnect, setNeedsReconnect] = useState(false);

    // Widget UI state
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isChatHidden, setIsChatHidden] = useState(false);
    const [activeView, setActiveView] = useState("list"); // "list" | "chat" | "newChat"
    const [activeChatId, setActiveChatId] = useState(null);
    const [activeChatName, setActiveChatName] = useState("");
    const [activeChatMembers, setActiveChatMembers] = useState([]);
    const [activeChatType, setActiveChatType] = useState("");
    const [creatingChat, setCreatingChat] = useState(false);

    // Data
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loadingChats, setLoadingChats] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [chatError, setChatError] = useState(null);

    /* Kod maszynowy błędu listy czatów (patrz utils/teamsBlad). Trzymamy go OBOK napisu, bo to
       kod — a nie treść zdania — decyduje, czy wolno zaproponować ponowne połączenie konta. */
    const [chatErrorKod, setChatErrorKod] = useState(null);

    /* Błąd JEDNEJ rozmowy: { chatId, kod }. Osobno od `chatError`, bo niedostępna rozmowa
       NIE MA PRAWA gasić listy czatów ani całego okna — to był właśnie zgłoszony defekt. */
    const [bladWiadomosci, setBladWiadomosci] = useState(null);

    /* Rozmowy, o których wiemy, że konto nie ma do nich dostępu: chatId → powód.
       Uzupełnia oznaczenie z backendu o to, co sami wykryliśmy przy próbie wejścia. */
    const [czatyNiedostepne, setCzatyNiedostepne] = useState({});


    // Obecność (presence) zalogowanego użytkownika — "Available"/"Busy"/"Away"/"PresenceUnknown"...
    const [myPresence, setMyPresence] = useState(null);

    // Polling refs
    const messagePollRef = useRef(null);

    // Notification refs
    const prevChatsUnreadRef = useRef(null); // mapa chatId → isUnread (poprzedni snapshot do detekcji nowych)
    const teamsNotifyCallbackRef = useRef(null);

    // ── Helpers ──

    /**
     * Sprawdza czy HTTP error to brak/wygasłe połączenie Microsoft.
     * Główny kontrakt: 409 z code=ms_connection_required (isMsConnectionRequired).
     * Fallback legacy: 401 z error=teams_auth_expired (starsze odpowiedzi backendu).
     */
    const isAuthExpiredError = useCallback((err) => {
        if (isMsConnectionRequired(err)) return true;
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.error;
        return status === 401 && errorCode === "teams_auth_expired";
    }, []);

    /** Obsługa wygasłego tokena — ustawia needsReconnect i stopuje polling */
    const handleAuthExpired = useCallback(() => {
        setIsConnected(false);
        setNeedsReconnect(true);
        setChatError(null); // nie wyświetlaj error text — pokaże się reconnect prompt
        setChatErrorKod(null);
        setChats([]);
    }, []);

    /* Zapamiętanie rozmowy bez dostępu. Wołane, gdy serwer odmówi wglądu w KONKRETNY czat —
       dzięki temu pozycja zostaje na liście wyszarzona (z powodem), a odpytywanie tej rozmowy
       milknie zamiast dobijać Graph tym samym 403 co 2,5 sekundy. */
    const oznaczCzatNiedostepny = useCallback((chatId, powod) => {
        if (!chatId) return;
        setCzatyNiedostepne((prev) => (
            prev[chatId] ? prev : { ...prev, [chatId]: powod || "Brak uprawnień do tej rozmowy" }
        ));
    }, []);

    /** Czy rozmowa jest niedostępna — oznaczona przez backend ALBO wykryta przy próbie wejścia. */
    const czyCzatNiedostepny = useCallback(
        (chat) => {
            if (!chat) return false;
            const id = typeof chat === "string" ? chat : chat.id;
            if (id && czatyNiedostepne[id]) return true;
            return typeof chat === "object" && czatOznaczonyNiedostepnym(chat);
        },
        [czatyNiedostepne]
    );

    /** Powód niedostępności — z backendu, a gdy go nie ma, z naszego wykrycia. */
    const powodCzatuNiedostepnego = useCallback(
        (chat) => {
            const id = typeof chat === "string" ? chat : chat?.id;
            if (typeof chat === "object" && chat && czatOznaczonyNiedostepnym(chat)) {
                return powodNiedostepnosciCzatu(chat);
            }
            return (id && czatyNiedostepne[id]) || powodNiedostepnosciCzatu(null);
        },
        [czatyNiedostepne]
    );

    /* 🔴 [H1 2026-09-19] TOŻSAMOŚĆ KONTA, A NIE OBIEKT KONTA, W ZALEŻNOŚCIACH.
       ZMIERZONE (zapadka `__tests__/sufitZadanLandingow.h1`, licznik na adapterze axiosa): wejście
       na landing IT wysyłało `GET /api/teams-chat/connection-status` DWA RAZY.
       MECHANIZM: `AuthContext.checkLoggedIn` woła `setUser` dwa razy na każde wejście — raz
       optymistycznie z localStorage, drugi raz po `GET /users/me` (warunek `!hadSignalRToken` po F5
       jest zawsze prawdziwy, bo token SignalR żyje wyłącznie w pamięci). Konto to samo, OBIEKT nowy
       — więc efekt z `user` w zależnościach leciał drugi raz. Ta sama naprawa, co w
       `components/Layout.js` (`idKonta`) i w `hooks/useModulyObslugi`. */
    const idKontaCzatu = tozsamoscKonta(user);

    // ── Check Teams connection on mount ──
    useEffect(() => {
        if (!idKontaCzatu) {
            setIsConnected(false);
            setConnectionChecked(false);
            setNeedsReconnect(false);
            return;
        }
        const check = async () => {
            try {
                const res = await apiService.getTeamsChatConnectionStatus();
                const connected = res.data?.isConnected ?? false;
                setIsConnected(connected);
                if (!connected) setNeedsReconnect(false);
            } catch {
                setIsConnected(false);
            }
            setConnectionChecked(true);
        };
        check();
    }, [idKontaCzatu]);

    // ── Refresh connection status (po reconnect) ──
    const refreshConnection = useCallback(async () => {
        try {
            const res = await apiService.getTeamsChatConnectionStatus();
            const connected = res.data?.isConnected ?? false;
            setIsConnected(connected);
            if (connected) {
                setNeedsReconnect(false);
                setChatError(null);
            }
            return connected;
        } catch {
            return false;
        }
    }, []);

    // ── Fetch chat list (+ detekcja nowych nieprzeczytanych → powiadomienie) ──
    const fetchChats = useCallback(async () => {
        if (!isConnected) return;
        try {
            setLoadingChats(prev => prev === false ? true : prev);
            setChatError(null);
            setChatErrorKod(null);
            const res = await apiService.getTeamsChats(30);
            const newChats = res.data || [];
            setChats(newChats);

            // Powiadomienie per-czat: które czaty stały się nieprzeczytane od ostatniego pobrania.
            const prevMap = prevChatsUnreadRef.current;
            const unreadNow = newChats.filter(c => c.isUnread).length;
            if (prevMap) {
                const newlyUnread = newChats.filter(c => c.isUnread && !prevMap[c.id]);
                if (newlyUnread.length > 0 && teamsNotifyCallbackRef.current) {
                    const first = newlyUnread[0];
                    const name = first?.topic
                        || first?.members?.find(m => m.displayName)?.displayName
                        || "Teams";
                    // Wzbogacony payload — dymek może otworzyć konkretny czat po kliknięciu.
                    teamsNotifyCallbackRef.current({
                        count: unreadNow,
                        chatId: first?.id,
                        chatName: name,
                        members: first?.members || [],
                        chatType: first?.chatType || "",
                        preview: stripHtmlPreview(first?.lastMessagePreview || first?.lastMessage || ""),
                    });
                }
            }
            prevChatsUnreadRef.current = Object.fromEntries(newChats.map(c => [c.id, !!c.isUnread]));
            return true; // udało się — odpytywanie może wrócić do normalnego tempa
        } catch (err) {
            console.error("Teams chat fetch error:", err);

            const status = err?.response?.status;
            const kod = kodBleduTeams(err);
            /* 🔴 ROZSTRZYGAMY PO KODZIE, NIE PO STATUSIE ANI PO NAPISIE.
               „Brak uprawnień” (403 InsufficientPrivileges z Graph) NIE JEST wygaśnięciem tokenu:
               wysłanie po nim człowieka na ponowne połączenie konta było kłamstwem o przyczynie.
               Bez kodu (stara odpowiedź) zostaje dotychczasowe rozpoznanie po statusie. */
            if (kod === KOD_TEAMS.BRAK_UPRAWNIEN || kod === KOD_TEAMS.NIEDOSTEPNE) {
                setChatErrorKod(kod);
                setChatError(opisBleduTeams(kod, { kontekst: "lista" }).tresc);
            } else if (kod === KOD_TEAMS.TOKEN_WYGASL || isAuthExpiredError(err) || status === 401) {
                handleAuthExpired();
            } else {
                setChatErrorKod(null);
                setChatError("Nie udało się pobrać czatów. Spróbuj ponownie.");
            }
            /* Zwracamy WYNIK zamiast tylko go logować. Bez tego odpytywanie nie miało jak
               poznać odmowy — a właśnie na niej opiera się wycofanie przy limicie żądań Graph. */
            return false;
        } finally {
            setLoadingChats(false);
        }
    }, [isConnected, isAuthExpiredError, handleAuthExpired]);

    // Paginacja historii: @odata.nextLink z ostatniego pobrania + flaga doładowywania.
    const [nextOlderLink, setNextOlderLink] = useState(null);
    const [loadingOlder, setLoadingOlder] = useState(false);

    // ── Fetch messages for active chat ──
    /* [polecenie właściciela 2026-09-29] „jak coś odczytam, to nie mam od razu synchronizacji z Teams”: otwarcie rozmowy oznaczało ją
       w Teams jako przeczytaną, ale wiadomości, które przychodziły, GDY ROZMOWA BYŁA OTWARTA, zostawały w Teams nieprzeczytane. Tu pamiętamy
       najnowszą wiadomość, dla której rozmowa jest już oznaczona; nowa (pobrana przy otwartej rozmowie) = ponowne oznaczenie. */
    const oznaczonaDoRef = useRef({});
    const fetchMessages = useCallback(async (chatId) => {
        if (!chatId || !isConnected) return;
        try {
            const res = await apiService.getTeamsChatMessages(chatId, 30);
            const incoming = (res.data?.messages || []).slice().reverse(); // API: newest-first → wyświetlamy oldest-first
            const najnowsza = incoming[incoming.length - 1];
            if (najnowsza?.id && oznaczonaDoRef.current[chatId] !== najnowsza.id) {
                const pierwszePobranie = oznaczonaDoRef.current[chatId] === undefined; // to pobranie po otwarciu — openChat już oznaczył
                oznaczonaDoRef.current[chatId] = najnowsza.id;
                if (!pierwszePobranie) apiService.markTeamsChatRead(chatId).catch(() => {});
            }
            setNextOlderLink(res.data?.nextLink || null);
            // Udało się — kasujemy ewentualny wcześniejszy błąd TEJ rozmowy.
            setBladWiadomosci((prev) => (prev && prev.chatId === chatId ? null : prev));
            // MERGE po id (nie pełny replace) — chroni wiadomość dodaną optymistycznie po wysyłce przed
            // zniknięciem, zanim Graph ją zaindeksuje (wyścig pollingu), i zachowuje doładowaną historię.
            setMessages((prev) => {
                if (prev.length === 0) return incoming;
                const incomingIds = new Set(incoming.map((m) => m.id));
                const extras = prev.filter((m) => !incomingIds.has(m.id)); // optymistyczne + starsze doładowane
                return [...extras, ...incoming].sort(
                    (a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime)
                );
            });
        } catch (err) {
            console.error("Teams messages fetch error:", err);
            const kod = kodBleduTeams(err);
            /* 🔴 JEDNA NIEDOSTĘPNA ROZMOWA NIE GASI OKNA.
               Tak wyglądał zgłoszony defekt: czat spotkania wracał z Graph jako 403
               (InsufficientPrivileges), front brał to za wygaśnięcie tokenu, kasował CAŁĄ listę
               czatów i pokazywał „Sesja Teams wygasła. Połącz ponownie”. Ponowne łączenie konta
               nie mogło pomóc, bo problem nie leżał w tokenie. Teraz błąd zostaje przy tej jednej
               rozmowie, a reszta widgetu pracuje dalej. */
            if (kod === KOD_TEAMS.BRAK_UPRAWNIEN) {
                setBladWiadomosci({ chatId, kod });
                oznaczCzatNiedostepny(chatId, powodNiedostepnosciCzatu(err?.response?.data));
            } else if (kod === KOD_TEAMS.TOKEN_WYGASL || isAuthExpiredError(err)) {
                handleAuthExpired();
            } else {
                setBladWiadomosci({ chatId, kod: kod || null });
            }
        } finally {
            setLoadingMessages(false);
        }
    }, [isConnected, isAuthExpiredError, handleAuthExpired, oznaczCzatNiedostepny]);

    // ── Doładuj starsze wiadomości (paginacja po @odata.nextLink z poprzedniej strony) ──
    const loadOlderMessages = useCallback(async () => {
        if (!activeChatId || !nextOlderLink || loadingOlder) return;
        setLoadingOlder(true);
        try {
            const res = await apiService.getTeamsChatMessages(activeChatId, 30, nextOlderLink);
            const older = (res.data?.messages || []).slice().reverse();
            setNextOlderLink(res.data?.nextLink || null);
            setMessages((prev) => {
                const prevIds = new Set(prev.map((m) => m.id));
                const fresh = older.filter((m) => !prevIds.has(m.id));
                return [...fresh, ...prev].sort(
                    (a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime)
                );
            });
        } catch (err) {
            // Doładowanie historii: tylko realne wygaśnięcie tokenu rozłącza konto.
            if (kodBleduTeams(err) === KOD_TEAMS.TOKEN_WYGASL || (isAuthExpiredError(err) && !kodBleduTeams(err))) {
                handleAuthExpired();
            }
        } finally {
            setLoadingOlder(false);
        }
    }, [activeChatId, nextOlderLink, loadingOlder, isAuthExpiredError, handleAuthExpired]);

    // ── Periodic connection re-check when disconnected ──
    // Po auto-connect lub ręcznym połączeniu, wykrywa nowe połączenie w ciągu kilku sekund
    useEffect(() => {
        if (!user || isConnected || !connectionChecked) return;

        const recheckConnection = async () => {
            try {
                const res = await apiService.getTeamsChatConnectionStatus();
                const connected = res.data?.isConnected ?? false;
                if (connected) {
                    setIsConnected(true);
                    setNeedsReconnect(false);
                    setChatError(null);
                }
            } catch {
                // Ignorujemy błędy — spróbuje ponownie za chwilę
            }
        };

        const interval = setInterval(recheckConnection, 10000); // co 10s
        return () => clearInterval(interval);
    }, [user, isConnected, connectionChecked]);

    // ── Poll chat list ZAWSZE gdy połączony ──
    // Otwarty widget = częściej (responsywność); zamknięty = rzadziej (tło) — żeby łapać
    // nowe wiadomości i odświeżać kolejność/podgląd nawet bez otwierania widgetu (+ powiadomienia).
    useEffect(() => {
        if (!isConnected) return;

        let timer = null;
        let wycofanie = 1;

        const odstep = () => (isWidgetOpen ? POLL_INTERVAL_CHAT_LIST : POLL_INTERVAL_BACKGROUND) * wycofanie;

        const zaplanuj = () => {
            if (timer) clearTimeout(timer);
            // Karta ukryta → nie planujemy nic. Wznowi ją zdarzenie powrotu (niżej).
            if (!czyKartaWidoczna()) return;
            timer = setTimeout(tik, odstep());
        };

        const tik = async () => {
            /* `fetchChats` obsługuje błędy u siebie i NIE rzuca — dlatego czytamy jego WYNIK.
               Gdyby polegać tu na wyjątku, wycofanie byłoby martwym kodem i nigdy by się
               nie uruchomiło, a skrócone odstępy dobijałyby Graph przy każdym limicie. */
            const ok = await fetchChats();
            wycofanie = ok === false
                ? Math.min(wycofanie * MNOZNIK_WYCOFANIA, MAKS_WYCOFANIE_MS / POLL_INTERVAL_CHAT_LIST)
                : 1;
            zaplanuj();
        };

        /* NAJWAŻNIEJSZA CZĘŚĆ TEJ NAPRAWY. Typowy scenariusz: człowiek pisze w Teams na
           komputerze i przełącza się do przeglądarki — dotąd czekał do 30 sekund na kolejne
           odpytanie. Teraz powrót do karty odświeża natychmiast. */
        const naPowrot = () => {
            if (!czyKartaWidoczna()) return;
            wycofanie = 1;
            fetchChats();
            zaplanuj();
        };

        fetchChats();
        zaplanuj();
        document.addEventListener("visibilitychange", naPowrot);
        window.addEventListener("focus", naPowrot);

        return () => {
            if (timer) clearTimeout(timer);
            document.removeEventListener("visibilitychange", naPowrot);
            window.removeEventListener("focus", naPowrot);
        };
    }, [isConnected, isWidgetOpen, fetchChats]);

    // ── Poll obecności (presence) gdy połączony ──
    useEffect(() => {
        if (!isConnected) return;
        let cancelled = false;
        const fetchPresence = async () => {
            try {
                const res = await apiService.getTeamsPresence();
                if (!cancelled) setMyPresence(res.data?.availability ?? null);
            } catch {
                if (!cancelled) setMyPresence(null);
            }
        };
        fetchPresence();
        const interval = setInterval(fetchPresence, POLL_INTERVAL_PRESENCE);
        return () => { cancelled = true; clearInterval(interval); };
    }, [isConnected]);

    // ── Poll messages when a chat is active ──
    useEffect(() => {
        /* Rozmowa, o której WIEMY, że konto nie ma do niej dostępu, nie jest odpytywana wcale.
           Wcześniej takie okno waliło w Graph tym samym 403 co 2,5 sekundy — i za każdym razem
           wywracało widget. Pokazujemy powód od razu, bez ani jednego zapytania. */
        if (activeChatId && czatyNiedostepne[activeChatId]) {
            setLoadingMessages(false);
            setBladWiadomosci({ chatId: activeChatId, kod: KOD_TEAMS.BRAK_UPRAWNIEN });
            return undefined;
        }
        if (activeChatId && isWidgetOpen && isConnected) {
            setLoadingMessages(true);
            fetchMessages(activeChatId);
            /* Odpytujemy tylko wtedy, gdy karta jest widoczna — w ukrytej i tak nikt tego nie
               zobaczy, a żądania zjadałyby limit Graph potrzebny gdzie indziej. Powrót do karty
               obsługuje efekt listy czatów wyżej, który odświeża natychmiast. */
            messagePollRef.current = setInterval(
                () => { if (czyKartaWidoczna()) fetchMessages(activeChatId); },
                POLL_INTERVAL_MESSAGES
            );
        }
        return () => {
            if (messagePollRef.current) {
                clearInterval(messagePollRef.current);
                messagePollRef.current = null;
            }
        };
    }, [activeChatId, isWidgetOpen, isConnected, fetchMessages, czatyNiedostepne]);

    // ── Actions ──
    const openChat = useCallback((chatId, chatName, members = [], chatType = "") => {
        setBladWiadomosci(null); // błąd poprzedniej rozmowy nie przechodzi na następną
        setActiveChatId(chatId);
        setActiveChatName(chatName || "");
        setActiveChatMembers(Array.isArray(members) ? members : []);
        setActiveChatType(chatType || "");
        setMessages([]);
        setActiveView("chat");
        // Otwarcie czatu = oznacz przeczytany w Teams + od razu zgaś lokalnie kropkę „nieprzeczytane"
        // (badge launchera dociągnie się przy najbliższym pollu). Fire-and-forget — błąd nie psuje otwierania.
        // markChatReadForUser nie działa dla czatów spotkań (19:...@unq.gbl.spaces) → pomijamy je.
        setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, isUnread: false } : c)));
        /* Rozmowy bez dostępu nie próbujemy nawet oznaczać jako przeczytanej — to właśnie
           `MarkChatReadForUser` sypał na produkcji osobnym ODataError obok błędu wiadomości. */
        if ((chatType || "").toLowerCase() !== "meeting" && !czatyNiedostepne[chatId]) {
            apiService.markTeamsChatRead(chatId).catch(() => {});
        }
    }, [czatyNiedostepne]);

    const backToList = useCallback(() => {
        setBladWiadomosci(null);
        setActiveChatId(null);
        setActiveChatName("");
        setActiveChatMembers([]);
        setActiveChatType("");
        setMessages([]);
        setActiveView("list");
    }, []);

    /**
     * Jednym kliknięciem otwiera (lub tworzy) czat 1:1 z osobą.
     * Graph dla oneOnOne jest idempotentny — zwraca istniejący czat, jeśli już jest,
     * więc nie powstają duplikaty. Najpierw próbujemy znaleźć istniejący czat lokalnie.
     */
    const openOrCreateChatWithUser = useCallback(async (person) => {
        if (!person?.email) return null;
        const emailLc = person.email.toLowerCase();

        // 1) Istniejący czat 1:1 z tą osobą na liście? Otwórz od razu.
        const existing = chats.find(c =>
            (c.chatType === "oneonone" || c.chatType === "oneOnOne") &&
            (c.members || []).some(m => (m.email || "").toLowerCase() === emailLc)
        );
        if (existing) {
            openChat(existing.id, person.displayName || existing.topic, existing.members, existing.chatType);
            return existing.id;
        }

        // 2) Utwórz (idempotentnie) czat 1:1.
        setCreatingChat(true);
        try {
            const res = await apiService.createTeamsChat({
                chatType: "oneOnOne",
                topic: null,
                memberEmails: [person.email],
            });
            if (res.data?.id) {
                openChat(res.data.id, person.displayName || "Czat", res.data.members || [], res.data.chatType);
                fetchChats();
                return res.data.id;
            }
            return null;
        } catch (err) {
            console.error("Nie udało się otworzyć czatu z osobą:", err);
            // Brak uprawnień do założenia rozmowy nie jest wygaśnięciem tokenu — nie wywracamy okna.
            if (kodBleduTeams(err) === KOD_TEAMS.TOKEN_WYGASL || (isAuthExpiredError(err) && !kodBleduTeams(err))) {
                handleAuthExpired();
            }
            return null;
        } finally {
            setCreatingChat(false);
        }
    }, [chats, openChat, fetchChats, isAuthExpiredError, handleAuthExpired]);

    const toggleWidget = useCallback(() => {
        setIsWidgetOpen(prev => !prev);
    }, []);

    const openWidget = useCallback(() => {
        setIsWidgetOpen(true);
    }, []);

    const toggleMaximize = useCallback(() => {
        setIsMaximized(prev => !prev);
    }, []);

    const closeWidget = useCallback(() => {
        setIsWidgetOpen(false);
        setIsMaximized(false);
    }, []);

    const sendMessage = useCallback(async (content) => {
        if (!activeChatId || !content.trim()) return null;
        setSendingMessage(true);
        try {
            const res = await apiService.sendTeamsChatMessage(activeChatId, content);
            if (res.data) {
                setMessages(prev => [...prev, res.data]);
            }
            return res.data;
        } catch (err) {
            const kod = kodBleduTeams(err);
            if (kod === KOD_TEAMS.BRAK_UPRAWNIEN) {
                // Odmowa zapisu w JEDNEJ rozmowie — nie rozłączamy konta.
                setBladWiadomosci({ chatId: activeChatId, kod });
                oznaczCzatNiedostepny(activeChatId, powodNiedostepnosciCzatu(err?.response?.data));
            } else if (kod === KOD_TEAMS.TOKEN_WYGASL || (isAuthExpiredError(err) && !kod)) {
                handleAuthExpired();
            }
            return null;
        } finally {
            setSendingMessage(false);
        }
    }, [activeChatId, isAuthExpiredError, handleAuthExpired, oznaczCzatNiedostepny]);

    /* [S20 2026-09-14] Przełączenie MOJEJ reakcji pod wiadomością aktywnej rozmowy: zmiana optymistyczna, żądanie
       (Graph setReaction / unsetReaction), przy błędzie cofnięcie. Po sukcesie świeży odczyt rozmowy — liczniki
       i osoby z serwera. Odmowa 403 NIE oznacza rozmowy jako niedostępnej (czytać ją można dalej) i NIE rozłącza
       konta; tylko wygaśnięcie tokenu prowadzi do ponownego połączenia. Zdanie dla człowieka składa ekran rozmowy. */
    const przelaczReakcje = useCallback(async (wiadomosc, znak) => {
        const chatId = activeChatId;
        const wynik = await przelaczReakcjeZSerwerem({
            api: apiService, chatId, wiadomosc, znak, ustawWiadomosci: setMessages,
        });
        if (wynik.ok) {
            fetchMessages(chatId);
            return wynik;
        }
        const kod = wynik.blad ? kodBleduTeams(wynik.blad) : null;
        if (wynik.blad && (kod === KOD_TEAMS.TOKEN_WYGASL || (isAuthExpiredError(wynik.blad) && !kod))) {
            handleAuthExpired();
        }
        return { ...wynik, kod };
    }, [activeChatId, fetchMessages, isAuthExpiredError, handleAuthExpired]);

    const setChatHidden = useCallback((hidden) => {
        setIsChatHidden(hidden);
    }, []);

    /** Rejestruje callback wywoływany gdy wzrasta liczba nieprzeczytanych */
    const registerTeamsNotifyCallback = useCallback((cb) => {
        teamsNotifyCallbackRef.current = cb;
        return () => { teamsNotifyCallbackRef.current = null; };
    }, []);

    /* Liczba nieprzeczytanych — POCHODNA listy czatów, nie osobny stan.
       Backend nie zalicza tu czatów spotkań: widget ich nie pokazuje, więc nie da się w nie
       wejść, a Teams nie pozwala oznaczyć ich jako przeczytane — jedno zdarzenie w takim
       czacie podnosiło czerwoną liczbę NA STAŁE i nic w aplikacji nie mogło jej zgasić. */
    const unreadCount = useMemo(() => chats.filter((c) => c.isUnread).length, [chats]);

    const value = useMemo(() => ({
        // Connection
        isConnected, connectionChecked, needsReconnect, refreshConnection,
        // Widget UI
        isWidgetOpen, toggleWidget, openWidget, closeWidget,
        isMaximized, toggleMaximize,
        isChatHidden, setChatHidden,
        activeView, setActiveView,
        // Chat list
        chats, loadingChats, fetchChats, chatError, chatErrorKod,
        // Rozmowy bez dostępu — lista rysuje je wyszarzone, okno rozmowy tłumaczy dlaczego
        czatyNiedostepne, czyCzatNiedostepny, powodCzatuNiedostepnego, oznaczCzatNiedostepny,
        bladWiadomosci,
        // Active chat
        activeChatId, activeChatName, activeChatMembers, activeChatType,
        openChat, backToList, openOrCreateChatWithUser, creatingChat,
        messages, loadingMessages, fetchMessages,
        // Paginacja historii
        loadOlderMessages, hasOlderMessages: !!nextOlderLink, loadingOlder,
        // Send
        sendMessage, sendingMessage,
        // [S20] Reakcje pod wiadomościami
        przelaczReakcje,
        /* Unread — LICZONY z listy czatów, a nie trzymany osobno.
           Osobny stan ustawiał się tylko przy pobraniu listy, więc otwarcie czatu gasiło
           kropkę przy rozmowie, ale czerwona liczba na bąbelku zostawała stara aż do
           najbliższego odpytania (do 30 sekund). Teraz rozjechać się nie mają jak. */
        unreadCount,
        // Presence
        myPresence,
        // Auth — pozwala komponentom (np. nowa rozmowa) zgłosić wygaśnięcie tokenu
        handleAuthExpired, isAuthExpiredError,
        // Notification callback
        registerTeamsNotifyCallback,
    }), [
        isConnected, connectionChecked, needsReconnect, refreshConnection,
        isWidgetOpen, toggleWidget, openWidget, closeWidget,
        isMaximized, toggleMaximize,
        isChatHidden, setChatHidden,
        activeView,
        chats, loadingChats, fetchChats, chatError, chatErrorKod,
        czatyNiedostepne, czyCzatNiedostepny, powodCzatuNiedostepnego, oznaczCzatNiedostepny,
        bladWiadomosci,
        activeChatId, activeChatName, activeChatMembers, activeChatType,
        openChat, backToList, openOrCreateChatWithUser, creatingChat,
        messages, loadingMessages, fetchMessages,
        loadOlderMessages, nextOlderLink, loadingOlder,
        sendMessage, sendingMessage,
        przelaczReakcje,
        unreadCount,
        myPresence,
        handleAuthExpired, isAuthExpiredError,
        registerTeamsNotifyCallback,
    ]);

    return (
        <TeamsChatContext.Provider value={value}>
            {children}
        </TeamsChatContext.Provider>
    );
};
